import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Elements of serverless – lambdas, queues, gateways, and more",
  "description": "Learn about the elements of your serverless ecosystem and how they fit together",
  "image": "./img/serverless-elements.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "elements-of-serverless--lambdas-queues-gateways-and-more"
    }}>{`Elements of serverless – lambdas, queues, gateways, and more`}</h1>
    <p><img parentName="p" {...{
        "src": "/e996d12745edd60ebf715646e6582d30/serverless-elements.svg",
        "alt": null
      }}></img></p>
    <p>{`Serverless is about combining small elements into a whole. But what are the elements and how do they fit together?`}</p>
    <p>{`We mentioned lambdas, queues, and a few other in previous chapters – `}<a parentName="p" {...{
        "href": "/serverless-architecture-principles"
      }}>{`Architecture Principles`}</a>{` and `}<a parentName="p" {...{
        "href": "/serverless-flavors"
      }}>{`Serverless Flavors`}</a>{`. Let's see how they work.`}</p>
    <h2 {...{
      "id": "lambda--a-cloud-function"
    }}>{`Lambda – a cloud function`}</h2>
    <p>{`"Lambda" comes from `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Lambda_calculus"
      }}>{`lambda calculus`}</a>{` – a mathematical definition of functional programming that Alonzo Church introduced in the 1930s. It's an alternative to Turing's `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Turing_machine"
      }}>{`turing machines`}</a>{`. Both describe a system that can `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Church%E2%80%93Turing_thesis"
      }}>{`solve any solvable problem`}</a>{`. Turing machines use iterative step-by-step programming, lambda calculus uses functions-calling-functions programming.`}</p>
    <p>{`Both are equal in power.`}</p>
    <p>{`AWS named their cloud functions AWS Lambda. As the platform grew in popularity, the word "lambda" morphed into a generic term for cloud functions. The core building block of serverless computing.`}</p>
    <p><strong parentName="p">{`A lambda is a function.`}</strong>{` In this context, a function running as its own tiny server triggered by an event.`}</p>
    <p>{`Here's a lambda function that returns `}<inlineCode parentName="p">{`"Hello world"`}</inlineCode>{` in response to an HTTP request.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/handler.ts

import { APIGatewayEvent } from "aws-lambda";

export const handler = async (event: APIGatewayEvent) => {
    return {
        statusCode: 200
        body: "Hello world"
    }
}
`}</code></pre>
    <p>{`The TypeScript file exports a function called `}<inlineCode parentName="p">{`handler`}</inlineCode>{`. The function accepts an event and returns a response. The AWS Lambda platform handles the rest.`}</p>
    <p>{`Because this is a user-facing API method, it accepts an AWS API Gateway event and returns an HTTP style response. Status code and body.`}</p>
    <p>{`Other providers and services have different events and expect different responses. A lambda always follows this pattern 👉 `}<strong parentName="p">{`function with an event and a return value`}</strong>{`.`}</p>
    <h3 {...{
      "id": "considerations-with-lambda-functions"
    }}>{`Considerations with lambda functions`}</h3>
    <p>{`Your functions should follow functional programming principles:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`idempotent`}</strong>{` – multiple calls with the same inputs produce the same result`}</li>
      <li parentName="ul"><strong parentName="li">{`pure`}</strong>{` – rely on the arguments you're given and nothing else. Your environment does not persist, data in local memory might vanish.`}</li>
      <li parentName="ul"><strong parentName="li">{`light on side-effects`}</strong>{` – you need side-effects to make changes like writing to a database. Make sure those come in the form of calling other functions and services. `}<em parentName="li">{`State inside your lambda does not persist`}</em></li>
      <li parentName="ul"><strong parentName="li">{`do one thing and one thing only`}</strong>{` – small functions focused on one task are easiest to understand and combine`}</li>
    </ul>
    <p>{`Small functions work together to produce extraordinary results. Like this example of `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/how-i-answer-the-door-with-aws-lambda-and-twilio/swizec/9255"
      }}>{`combining Twilio and AWS Lambda to answer the door`}</a>{`.`}</p>
    <h3 {...{
      "id": "creating-lambdas"
    }}>{`Creating lambdas`}</h3>
    <p>{`In the open source Serverless Framework, you define lambda functions with `}<inlineCode parentName="p">{`serverless.yml`}</inlineCode>{` like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml"
      }}>{`functions:
    helloworld:
        handler: dist/helloworld.handler
        events:
            - http:
                  path: helloworld
                  method: GET
                  cors: true
`}</code></pre>
    <p>{`Define a `}<inlineCode parentName="p">{`helloworld`}</inlineCode>{` function and say it maps to the `}<inlineCode parentName="p">{`handler`}</inlineCode>{` method exported from `}<inlineCode parentName="p">{`dist/helloworld`}</inlineCode>{`. We're using a build step for TypeScript – the code is in `}<inlineCode parentName="p">{`src/`}</inlineCode>{`, we run it from `}<inlineCode parentName="p">{`dist/`}</inlineCode>{`.`}</p>
    <p><inlineCode parentName="p">{`events`}</inlineCode>{` lists the triggers that run this function. An HTTP GET request on the path `}<inlineCode parentName="p">{`/helloworld`}</inlineCode>{` in our case.`}</p>
    <p>{`Other typical triggers include Queues, S3 changes, CloudWatch events, and DynamoDB listeners. At least on AWS.`}</p>
    <h2 {...{
      "id": "queue"
    }}>{`Queue`}</h2>
    <p>{`Queue is short for `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Message_queue"
      }}>{`message queue`}</a>{` – a service built on top of `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Queue_(abstract_data_type)"
      }}>{`queue, the data structure`}</a>{`. Software engineers aren't that inventive with names 🤷‍♂️`}</p>
    <p>{`You can think of the queue data structure as a list of items.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/770b9a52bfd8bece489567df4f178db2/bcb2d/queue.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.52914798206278%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'137\\'%20viewBox=\\'0%200%20400%20137\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M27%2023c-7%201-12%203-12%205l1%201c2%200%201%201-1%206-3%208-3%209%201%209a242%20242%200%200138-5c0-1%202-17%201-18s-21%200-28%202m5%201c-10%202-13%203-12%205v2l-2%203-2%204c-1%204-1%205%203%203l18-2%2015-2%201-7v-7h-8l-13%201m30%206l-2%201h2c9-2%2020%209%2023%2023v4l-3-4c-3-4-5-3-2%202%202%204%206%207%208%205%202-1%206-7%204-7l-2%203-2%204-2-8c-4-16-13-25-24-23m164%2032c-7%201-21%207-16%207l1%2010c0%2011%200%2011%206%209s17-4%2029-5h12l2-6%203-10%201-3-9-2h-29m51%201c-7%201-11%203-6%203l-2%209c-4%2013-3%2015%207%2010%209-4%2019-6%2030-7%2011%200%2011%200%2011-8%200-7%200-7-8-7a24692%2024692%200%2001-32%200m-95%201c-11%201-17%201-22%203l-7%201-2%201c-2%202-2%202-1%203v8c-3%2013-3%2015%204%2012%207-4%2037-6%2043-4%202%201%202%201%202-2l2-12c1-9%201-11-2-11l-17%201m99%200l-6%202-6%2018c0%203%201%203%207%200%209-4%2021-7%2030-7l9-1V65h-8c-19-2-22-2-26-1m-83%201l-16%201c-11%201-16%201-22%203l-7%202-1%208c-3%2011-3%2013%201%2011%207-3%2035-6%2042-4%203%201%203%201%203-4l1-8c2-6%201-10-1-9m21%201l-6%202v19l4-1c5-2%2022-5%2031-5h8l2-5%204-8v-3h-8c-16-2-29-1-35%201m-99%201c-23%200-23%200-24%202-1%203-5%2024-4%2025s1-1%203-9c3-16%203-16%207-16h39l-1%2011v12l-4-1c-14-3-46-1-42%203l9-2c9-1%2025-1%2033%201l6%201%201-12%201-13c1-2%200-3-1-3l-23%201m216%202l-8%203h2c7-3%2015-2%2017%201%201%203%202%2012%200%2015-1%201-1%201-2-2-1-4-5-6-3-2%201%205%204%209%206%209s7-8%207-10l-4%203c-3%206-4%206-2%202V73c-2-4-8-6-13-4m9%2032a73113%2073113%200%2001-11%204h2v6c-2%2011-3%2011%2022%2010h22v-18c-1-5-16-6-35-2m5%201c-12%203-13%203-13%2014v4h8l21-1h13l-1-7-1-9c-2-2-20-2-27-1\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/770b9a52bfd8bece489567df4f178db2/ca0a1/queue.webp 223w", "/static/770b9a52bfd8bece489567df4f178db2/75680/queue.webp 445w", "/static/770b9a52bfd8bece489567df4f178db2/8d1ba/queue.webp 890w", "/static/770b9a52bfd8bece489567df4f178db2/3838e/queue.webp 1335w", "/static/770b9a52bfd8bece489567df4f178db2/e11e5/queue.webp 1780w", "/static/770b9a52bfd8bece489567df4f178db2/bcddc/queue.webp 3343w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/770b9a52bfd8bece489567df4f178db2/e92b6/queue.png 223w", "/static/770b9a52bfd8bece489567df4f178db2/e66bf/queue.png 445w", "/static/770b9a52bfd8bece489567df4f178db2/4ef49/queue.png 890w", "/static/770b9a52bfd8bece489567df4f178db2/4e814/queue.png 1335w", "/static/770b9a52bfd8bece489567df4f178db2/701e9/queue.png 1780w", "/static/770b9a52bfd8bece489567df4f178db2/bcb2d/queue.png 3343w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/770b9a52bfd8bece489567df4f178db2/4ef49/queue.png",
              "alt": "queue",
              "title": "queue",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p><inlineCode parentName="p">{`enqueing`}</inlineCode>{` adds items to the back of a queue, `}<inlineCode parentName="p">{`dequeing`}</inlineCode>{` takes them out the front. Items in the middle wait their turn. Like a lunch-time burrito queue. First come first serve, FIFO for short (first in first out).`}</p>
    <div id="lock" />
    <p><strong parentName="p">{`A messaging queue takes this data structure and scales it into a service.`}</strong>{` `}</p>
    <p>{`Different implementations exist and they all share these core properties:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`persistent storage`}</strong>{` – queues have to be reliable and store messages in a database. Most queues prioritize speed and use in-memory storage like Redis.`}</li>
      <li parentName="ul"><strong parentName="li">{`a worker process`}</strong>{` – once you have messages, you need to process them. A process periodically checks, if there's something new `}<a parentName="li" {...{
          "href": "https://en.wikipedia.org/wiki/Polling_(computer_science)"
        }}>{`(polling)`}</a>{`. Another approach is to trigger this check every time a message arrives.`}</li>
      <li parentName="ul"><strong parentName="li">{`a trigger API`}</strong>{` – when the queue sees a new message, it runs your code. In serverless that means running your lambda, in traditional environments it's another worker process.`}</li>
      <li parentName="ul"><strong parentName="li">{`a retry/error policy`}</strong>{` – queues help you deal with errors. When you fail to process a message, it goes back on the queue and gets retried later.`}</li>
    </ul>
    <p>{`Many modern queues add time to the mix. You can `}<em parentName="p">{`schedule`}</em>{` messages for later. 2 seconds, 2 minutes, 2 days, ... Some queues limit how long messages can stick around.`}</p>
    <h3 {...{
      "id": "time-helps-with-errors"
    }}>{`Time helps with errors`}</h3>
    <p>{`Server processes can `}<a parentName="p" {...{
        "href": "/architecture-principles"
      }}>{`fail for any reason at any time`}</a>{`. For temporary errors a queue can use `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Exponential_backoff"
      }}>{`exponential backoff`}</a>{` when retrying. Giving your system more and more time to recover from issues.`}</p>
    <p>{`Try to process a message. It fails. Try after 1 second. Fails. Retry in 2 seconds. Fail. 4 seconds ...`}</p>
    <p>{`I've seen corrupt messages, known as poison pills, backing off days into the future. We talk about handling unprocessable messages in the `}<a parentName="p" {...{
        "href": "/robust-backend-design"
      }}>{`Robust Backend Design`}</a>{` chapter.`}</p>
    <h3 {...{
      "id": "defining-a-queue"
    }}>{`Defining a queue`}</h3>
    <p>{`AWS SimpleQueueService is a great queue service for the AWS serverless ecosystem. More powerful alternatives exist, but require more setup, more upkeep, and have features you might not need.`}</p>
    <blockquote>
      <p parentName="blockquote">{`Always use the simplest and smallest service that solves your problem until you know `}<em parentName="p">{`why`}</em>{` you need more ✌️`}</p>
    </blockquote>
    <p>{`Using the serverless framework, you define an SQS queue in the `}<inlineCode parentName="p">{`resources`}</inlineCode>{` section like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml"
      }}>{`resources:
    Resources:
        MyQueue:
            Type: "AWS::SQS::Queue"
            Properties:
                QueueName: "MyQueue-\${self:provider.stage}"
`}</code></pre>
    <p>{`A resource named `}<inlineCode parentName="p">{`MyQueue`}</inlineCode>{` of the SQS Queue type with a queue name of `}<inlineCode parentName="p">{`MyQueue-{stage}`}</inlineCode>{`. Adding the stage to your queue name allows different logical environments without collisions. More on that in `}<a parentName="p" {...{
        "href": "/dev-qa-prod"
      }}>{`the Dev, QA, and Prod chapter`}</a></p>
    <p>{`Make sure you use correct capitalization. The second `}<inlineCode parentName="p">{`Resources`}</inlineCode>{` must be capitalized, so do `}<inlineCode parentName="p">{`Type`}</inlineCode>{`, `}<inlineCode parentName="p">{`Properties`}</inlineCode>{`, and `}<inlineCode parentName="p">{`QueueName`}</inlineCode>{`. `}</p>
    <p>{`Capitalization matters because you're dropping a layer below Serverless and writing AWS's native Serverless Application Model (SAM) configuration. It's what your config compiles to before deploying.`}</p>
    <h3 {...{
      "id": "processing-a-queue"
    }}>{`Processing a queue`}</h3>
    <p>{`You need a lambda to process messages on `}<inlineCode parentName="p">{`MyQueue`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml"
      }}>{`functions:
    myQueueProcess:
        handler: dist/lambdas/myQueue.handler
        events:
            - sqs:
                  arn:
                      Fn::GetAtt:
                          - MyQueue
                          - Arn
                  batchSize: 1
`}</code></pre>
    <p>{`A lambda that runs each time `}<inlineCode parentName="p">{`MyQueue`}</inlineCode>{` has a new message to process. With a `}<inlineCode parentName="p">{`batchSize`}</inlineCode>{` of 1, each message runs its own lambda – a good practice for initial implementations. More about batch sizes in the `}<a parentName="p" {...{
        "href": "/lambda-workflows"
      }}>{`Lambda Workflows`}</a>{` and `}<a parentName="p" {...{
        "href": "/robust-backend-design"
      }}>{`Robust Backend Design`}</a>{` chapters.`}</p>
    <p>{`The strange yaml syntax reads as: `}<em parentName="p">{`an SQS event fired by a queue with the ARN identifier of getAttribute(Arn) from MyQueue`}</em>{`. Amazon Resource Names, ARN, are unique identifiers for each resource in your AWS account.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`myQueue.handler`}</inlineCode>{` lambda would look like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { SQSEvent, SQSRecord } from "aws-lambda";

export const handler = async (event: SQSEvent) => {
    // N depends on batchSize setting
    const messages: string[] = event.Records.map(
        (record: SQSRecord) => record.body
    );

    // do something with each message
    // throw Error() on fail

    return true;
};
`}</code></pre>
    <p>{`An async function that accepts an `}<inlineCode parentName="p">{`SQSEvent`}</inlineCode>{`, which contains multiple messages depending on `}<inlineCode parentName="p">{`batchSize`}</inlineCode>{`. Never assume the total number because that setting might change.`}</p>
    <p>{`Extract messages into an array of strings and process with a loop. Throw an error when something goes wrong so SQS can retry. Return true on success.`}</p>
    <h3 {...{
      "id": "sns"
    }}>{`SNS`}</h3>
    <p>{`A useful alternative to SQS is the Simple Notification Service – SQS. Similar behavior, except you can't store messages on the queue.`}</p>
    <p>{`With SNS each message sends `}<em parentName="p">{`once`}</em>{`. If you don't catch it, it's gone.`}</p>
    <p>{`But unlike SQS, each message can trigger multiple services.`}</p>
    <h2 {...{
      "id": "api-gateway"
    }}>{`API Gateway`}</h2>
    <p>{`You might not realize this, but servers don't talk directly to the internet.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media3.giphy.com/media/JR6T8yu6jnO8PBCBY1/giphy-loop.mp4?cid=4ac046a27jqraz0buvkuw6iy8gizlqry1bz9trhq5hsmvkkz&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Application servers are protected by a `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Reverse_proxy"
      }}>{`reverse proxy`}</a>{` – a server that specializes in taking raw internet requests and routing them.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/a0be4f3df041f9564f122f5a1368d2e4/9b29b/api-gateway.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "47.98206278026906%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'192\\'%20viewBox=\\'0%200%20400%20192\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M313%2012c0%201%200%202%201%201%201%200%203%201%204%204l2%204c-2%201-3%202-2%2010v17c-2%2019-2%2025-1%2028%202%204%208%206%2017%206%2012%200%2012%200%2012-24%200-35-1-38-16-38-7%200-8%200-9-2%200-2-3-5-6-7-2-1-2-1-2%201m-40%2010c-2%201-2%201-1%2010%200%208%200%208-1%207h-2l1%201c2%201%202%207%200%2018-1%2010-1%2017%201%2020%204%206%2022%208%2026%204%203-4%203-49%200-55-3-5-16-8-24-5m48%201l-1%2012v20c-4%2021-2%2025%2014%2025%2011%200%2010%201%2010-26-1-25-1-28-6-30-4-2-15-3-17-1m-46%201c-2%201-2%203-1%2012%200%209%200%2015-2%2023-2%2018%200%2023%2015%2023%2011%200%2010%201%2010-27-1-27-1-28-7-31h-15M50%2027c-7%204-16%2015-16%2022l-3%207c-5%207-7%2014-7%2024-1%209%202%2013%207%2013%202%200%203%200%203%207%200%2015%2013%2021%2027%2012l5-3%201%203c2%205%205%206%2016%207%2011%200%2018-2%2022-7%202-3%203-3%206-2%204%202%2012%201%2017-2%207-5%2011-14%208-18-2-1-2-2%202-5%206-4%209-9%209-12-1-3-8-7-13-7h-3l4-5c3-2%205-5%205-6%200-3-5-8-9-9l-5-3-3-4c-3-3-10-4-16-2-3%202-4%202-5%200-2-2-4-1-9%201-4%203-5%203-5-3%200-9-9-10-17-1-4%204-5%204-5%200-2-7-8-10-16-7m-2%204c-5%203-9%208-11%2013s-1%205%202%203c3-3%206-2%207%201%202%206-6%2011-10%208-2-2-2-2-4%201-5%207-9%2023-6%2030%202%205%207%206%2011%201%202-4%204-5%205-3s-1%205-4%207c-5%202-3%2017%203%2020%207%204%2023%200%2025-7%201-4%203-2%202%202%200%208%207%2011%2022%2010%209-1%2018-7%2015-10-2-2-1-6%201-7%202%200%202%200%202%204%201%209%2020%205%2026-5%204-7%201-10-5-8-4%202-2-1%202-3l9-6c9-9%207-12-7-15-5%200-5-2%201-6l4-6c0-5-9-10-15-7-3%201-4%200-1-2%205-3-1-10-9-9-6%201-10%204-10%207l-3%2010%201-5v-6c-1%200-6%204-6%206l-1%201c-3%200-2-2%203-6l4-5c0-2-4-1-8%202-5%203-5%203-6-5-1-5-2-6-5-6s-5%201-10%205-5%204-7%202l-1-3-2-3c-3-4-7-4-14%200m158%209c-19%202-19%201-19%2024l-2%2033c0%2017%201%2031%204%2034l8%201%2017-1c11%200%2011%200%2013-10l1-15c-2-23-2-36-1-44%201-10%201-18-3-21-2-2-3-2-18-1m1%202c-11%201-15%202-16%204-1%201-2%207-4%2049%200%2017%201%2034%203%2035l17-1h16l2-6c1-6%201-13-1-33V64c2-14%201-19-2-22h-15m70%2051c-2%201-2%202-2%2017l-1%2019c-2%206-1%2017%200%2020%202%203%207%205%2015%206%207%200%208%200%2011-2l3-3v-15c0-20-1-35-4-38-4-5-16-7-22-4m2%202c-2%201-2%202-2%2018l-2%2025c0%2012%203%2015%2016%2015%2010%200%2010-1%209-26%200-24-1-28-4-30s-13-3-17-2m42%202c-2%201-3%202-2%2010v17c-2%2018-2%2025-1%2028%202%204%208%206%2017%206%207%200%207%200%2010-2l2-3v-24c-1-26-1-28-6-31-3-2-16-3-20-1m1%202v12l-1%2020c-4%2021-2%2025%2014%2025%2011%200%2010%201%2010-26-1-25-1-28-6-30-4-2-15-3-17-1\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/a0be4f3df041f9564f122f5a1368d2e4/ca0a1/api-gateway.webp 223w", "/static/a0be4f3df041f9564f122f5a1368d2e4/75680/api-gateway.webp 445w", "/static/a0be4f3df041f9564f122f5a1368d2e4/8d1ba/api-gateway.webp 890w", "/static/a0be4f3df041f9564f122f5a1368d2e4/3838e/api-gateway.webp 1335w", "/static/a0be4f3df041f9564f122f5a1368d2e4/e11e5/api-gateway.webp 1780w", "/static/a0be4f3df041f9564f122f5a1368d2e4/93e07/api-gateway.webp 3840w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/a0be4f3df041f9564f122f5a1368d2e4/e92b6/api-gateway.png 223w", "/static/a0be4f3df041f9564f122f5a1368d2e4/e66bf/api-gateway.png 445w", "/static/a0be4f3df041f9564f122f5a1368d2e4/4ef49/api-gateway.png 890w", "/static/a0be4f3df041f9564f122f5a1368d2e4/4e814/api-gateway.png 1335w", "/static/a0be4f3df041f9564f122f5a1368d2e4/701e9/api-gateway.png 1780w", "/static/a0be4f3df041f9564f122f5a1368d2e4/9b29b/api-gateway.png 3840w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/a0be4f3df041f9564f122f5a1368d2e4/4ef49/api-gateway.png",
              "alt": "api gateway",
              "title": "api gateway",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`Requests come from the wild internet into the proxy. The proxy then decides:`}</p>
    <ul>
      <li parentName="ul">{`is this a valid request?`}</li>
      <li parentName="ul">{`will this request break the system?`}</li>
      <li parentName="ul">{`which type of server can handle this?`}</li>
      <li parentName="ul">{`which instance of that server should do it?`}</li>
    </ul>
    <p>{`Validating requests can involve checking for denial of service attacks, verifying permissions, firewall protection, and even A/B testing.`}</p>
    <p>{`Once verified the request goes to an instance of your application server. This is where `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Scalability#HORIZONTAL-SCALING"
      }}>{`horizontal scaling`}</a>{` comes into play.`}</p>
    <p>{`Amazon calls their reverse proxy service API Gateway. `}</p>
    <p>{`Other providers might have different names and they all perform the same function: Take request from the internet and pass it on to your lambda.`}</p>
    <h2 {...{
      "id": "static-file-storage--s3"
    }}>{`Static file storage – S3`}</h2>
    <p>{`The serverless world is ephemeral which means you can't save files just anywhere.`}</p>
    <p>{`Adding large files to your code makes starting new containers slow. You can't save locally because your server disappears after each request.`}</p>
    <p>{`Static file storage services solve that problem. `}</p>
    <p>{`AWS S3 is the most common. Other hosting services often act as an S3 abstraction. Pundits have called S3 the 8th wonder of the world.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`I know that `}<a parentName="p" {...{
          "href": "https://twitter.com/awscloud"
        }}>{`@awscloud`}</a>{` S3 is basically the eighth wonder of the world, but Athena is pretty damned close behind it. `}<br parentName="p"></br><br parentName="p"></br>{`Amazing, amazing product.`}</p>{`— Corey Quinn (@QuinnyPig) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/QuinnyPig/status/1220805676168970246"
      }}>{`January 24, 2020`}</a></blockquote>
    <p>{`You can think of S3 as a hard drive with an API. Read and write files, get their URL, change permissions, etc.`}</p>
    <p>{`Each file gets a URL that's backed by a server optimized for static files. No code, no dynamic changes. A raw file flying through HTTP.`}</p>
    <h2 {...{
      "id": "static-file-server--cdn"
    }}>{`Static file server – CDN`}</h2>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/184b1a00680fc15a6dedf920cc6867ae/7b3c8/cdn.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "87.89237668161435%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'351\\'%20viewBox=\\'0%200%20400%20351\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M240%2020c-5%206-3%2013%205%2013%204%200%209-5%209-9%200-5-9-8-14-4M99%2028c-6%206-3%2012%205%2012%207%200%2012-9%207-12s-9-3-12%200m148%2035c0%208%203%2030%207%2043%201%205%205%2011%207%2011l4-4%202-5-1-1-2%203-2%203c-2%200-7-7-8-11a399%20399%200%2001-7-39m81-1c-5%205-5%2010%200%2012s10%201%2013-5c1-4%200-7-5-8s-6-1-8%201m-211%201l3%205%205%2019v2c-3-4-3-1-1%203l3%204-5-1c-7%200-7%200-10%2012-3%2013-2%2015%205%2015h16l12%201v-4l1-17c0-2-4-3-10-4-8-1-8-1-5-7%203-5%202-7-1-2l-2%203-1-6c-2-8-4-15-7-19l-3-4m152%2022l2%209%202%2013c0%207%200%207-2%207-3%200-5%204-7%2017-2%209-2%209%2018%209l16%201v-3l1-11c0-8-2-10-7-10l-17-4-1-9c-1-8-5-23-5-19m-155%2021c-3%2014-4%2013%2013%2014h15l2-8%201-9-3-2-6-1-8-1-8-2h-4l-2%209m-14%203l-14%205-10%203c-2%200-11%207-11%209s5%203%208%201c1-1%201-1-3-1s-4%200-2-1l5-4a101%20101%200%200137-11c-1-2-4-2-10-1m169%209c-2%204-4%2014-4%2017v3h12l16%201h3l1-9c0-9%200-11-6-11l-10-2c-10-3-11-3-12%201m-228%206c-6%205-3%2012%205%2012%206%200%2012-9%207-12s-9-3-12%200m60-1c-4%202-11%206-9%206l4-1h2c-1%203-22%2013-30%2014l-8%202%201%201%209-2c9-2%2027-10%2030-15s6-5%204%200l-1%203h1l2-4c4-5%202-6-5-4m205%2017l6%201c5%201%207%201%2012%205%206%205%2014%208%2020%2010l4%202%201%201%201-2-8-4-14-7-9-6h-13m-7%206l9%208c12%2010%2020%2013%2033%2013h7l-4%202c-3%201-5%203-5%204l10-5c2-3-3-6-7-6v2c2%202%201%202-8%201-10-1-14-4-21-10-7-5-14-11-16-11l2%202m60%203c-4%204-3%209%201%2011l4%203-3%201c-4%202-7%207-6%209%201%201%201%200%202-2l5-5c4-3%207-1%207%205%200%203%201%205%202%205%202%200%200-10-3-12-2-2-3-3-1-3s6-4%206-7c2-6-9-10-14-5m-191%206l-2%203-1-2c-1-2-1-2%200%200v25c0%2020%200%2022%202%2023%203%203%208%204%2014%204%2011%200%2010%202%2014-42%200-9%200-10-10-11-14-2-15-2-17%200m1%202c-1%203-3%2039-2%2043%200%204%202%205%2012%205%2010%201%2011%200%2011-11l1-17%201-14%201-4-7-2c-14-2-16-2-17%200m-34%20112c2%202%202%202-9%202-10%201-23%203-36%207-12%204-7%203%208-1%2016-4%2032-6%2041-4l6%201-4%201c-3%201-5%204-3%204%203%200%2010-5%2010-6%200-2-6-5-11-6-3%200-3%200-2%202m26-1c-2%202-5%2017-5%2020%200%205%200%205%2017%206h16c1%200%202-1%202-9l1-10-2-3-7-2-8-2c-6-1-14-2-14%200m-1%209l-2%2011%201%203h15l15%201v-4l1-9c0-5-2-7-8-7l-7-2-9-1h-5l-1%208m-14%201l-16%204-22%205c-12%203-17%205-24%2010l-6%202-4%201c-3%204-3%205%202%204l9-1%205-1h-5l-8%201h-3c0-1%203-3%204-2l8-4c8-5%2010-6%2024-9a956%20956%200%200036-10m-98%2011c-4%205-2%209%204%2011l2%201-4%202-5%206c-1%204%200%204%203%200%204-8%2010-7%2010%200%200%203%201%207%202%206%202-2-1-11-3-13l-1-2c2%200%207-6%207-9%200-5-11-7-15-2m153%202c-9%202-10%205-4%207%202%201%206%202%207%204l5%203c2%200%201-3-1-3-5-2%200-2%206%200%208%202%2013%202%2013%201l-3-1-6-1-9-3c-9-1-14-5-5-5l5-1c0-2-3-2-8-1m36%204c-4%205-2%2011%204%2011%202%200%202%200-2%203s-7%208-5%209l1-1%207-8c3%200%205%202%205%206l2%206c2%200%200-10-3-12-2-2-2-3-1-3%202%200%207-6%207-9%200-5-11-7-15-2m-52%203c0%201%209%207%2017%209%206%203%208%203%2016%203%208-1%208-1%206%201-3%202-2%204%201%201%205-3%202-6-6-6-2%200-3%200-3%202%202%203-19-3-27-9l-4-1m-67%2017c-5%205-4%209%202%2011l3%202-3%201c-4%202-7%207-6%209%201%201%201%200%202-2l5-5c4-3%206-1%207%205l1%205c2%200%200-9-2-12-3-2-3-2-1-2%203-1%206-6%206-9%200-6-10-8-14-3\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/184b1a00680fc15a6dedf920cc6867ae/ca0a1/cdn.webp 223w", "/static/184b1a00680fc15a6dedf920cc6867ae/75680/cdn.webp 445w", "/static/184b1a00680fc15a6dedf920cc6867ae/8d1ba/cdn.webp 890w", "/static/184b1a00680fc15a6dedf920cc6867ae/3838e/cdn.webp 1335w", "/static/184b1a00680fc15a6dedf920cc6867ae/e11e5/cdn.webp 1780w", "/static/184b1a00680fc15a6dedf920cc6867ae/f200f/cdn.webp 3131w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/184b1a00680fc15a6dedf920cc6867ae/e92b6/cdn.png 223w", "/static/184b1a00680fc15a6dedf920cc6867ae/e66bf/cdn.png 445w", "/static/184b1a00680fc15a6dedf920cc6867ae/4ef49/cdn.png 890w", "/static/184b1a00680fc15a6dedf920cc6867ae/4e814/cdn.png 1335w", "/static/184b1a00680fc15a6dedf920cc6867ae/701e9/cdn.png 1780w", "/static/184b1a00680fc15a6dedf920cc6867ae/7b3c8/cdn.png 3131w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/184b1a00680fc15a6dedf920cc6867ae/4ef49/cdn.png",
              "alt": "cdn",
              "title": "cdn",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`CDNs – `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Content_delivery_network"
      }}>{`content delivery networks`}</a>{` – are the next step in serving static file. They're like a distributed caching system.`}</p>
    <p>{`Where S3 serves files from a central location, a CDN serves those same files from as close to the end user as possible. That speeds up your website by reducing latency.`}</p>
    <p>{`Configuration on your end goes like this:`}</p>
    <ol>
      <li parentName="ol">{`Point the CDN to your static file URL`}</li>
      <li parentName="ol">{`The CDN gives you a new URL`}</li>
      <li parentName="ol">{`Use `}<em parentName="li">{`that`}</em>{` URL in your client-side code`}</li>
    </ol>
    <p>{`You can automate this part with build tools. Netlify and Vercel both handle it for you.`}</p>
    <p>{`Now when a browser requests a file, the URL resolves to the nearest server. Request goes to that server and if the file is there, it's served. If there's no file, the CDN goes to your original source, caches the file, and `}<em parentName="p">{`then`}</em>{` sends it back to the user.`}</p>
    <p>{`And now your JavaScript, HTML, images, fonts, and CSS are fast to load anywhere in the world. 👌`}</p>
    <h2 {...{
      "id": "logging"
    }}>{`Logging`}</h2>
    <p>{`Logging is one of the hardest problems in a distributed multi-service world. You can't print to the console or write to a local file because you can't see the console and files vanish after every request.`}</p>
    <p>{`What do you do?`}</p>
    <p>{`You use a logging service to send logs to a central location.`}</p>
    <p>{`Implementing a logging service yourself is tricky (I've done it, do not recommend). The AWS ecosystem has you covered with CloudWatch.`}</p>
    <p>{`CloudWatch UI tools lack filtering and graphing features you'd want as a power user, but it's a great start.`}</p>
    <p>{`Anything you `}<inlineCode parentName="p">{`console.log`}</inlineCode>{` is collected in CloudWatch alongside default logs.`}</p>
    <h1 {...{
      "id": "those-are-the-important-elements"
    }}>{`Those are the important elements`}</h1>
    <p>{`Now you know the most important elements of your serverless ecosystem:`}</p>
    <ul>
      <li parentName="ul">{`lambdas for doing things`}</li>
      <li parentName="ul">{`queues for communicating`}</li>
      <li parentName="ul">{`gateways for handling requests`}</li>
      <li parentName="ul">{`S3 for static files`}</li>
      <li parentName="ul">{`CDN for serving static files`}</li>
      <li parentName="ul">{`logging to keep track`}</li>
    </ul>
    <p>{`There's a bunch more to discover, but that's the core. Next chapter we look at using these to build a robust system.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      